import { Controller } from "@hotwired/stimulus"

// Opens blog images in an overlay instead of a new browser tab. Attach to a
// container (e.g. the blog <article>); each image link is an `image` target and
// carries `data-action="click->lightbox#open"`, keeping its href as a no-JS
// fallback. Builds a native <dialog> on demand so Escape and the backdrop close
// for free, and lets the arrow keys / on-screen buttons step through the gallery.
export default class extends Controller {
  static targets = ["image"]

  open(event) {
    const link = event.currentTarget
    if (!link.getAttribute("href")) return

    event.preventDefault()
    this.index = this.imageTargets.indexOf(link)
    this.show()
  }

  show() {
    const dialog = document.createElement("dialog")
    dialog.className =
      "lightbox bg-transparent p-0 m-auto max-w-none max-h-none backdrop:bg-black/80"

    this.img = document.createElement("img")
    this.img.alt = ""
    this.img.className =
      "block max-w-[92vw] max-h-[88vh] w-auto h-auto rounded-md shadow-2xl"

    // Caption overlaid on the image, fed per-image from each link's
    // data-caption (set when a text row is paired with the image row).
    this.caption = document.createElement("div")
    this.caption.className =
      "absolute inset-x-0 bottom-0 p-5 sm:p-6 pt-10 rounded-b-md " +
      "bg-gradient-to-t from-black/80 via-black/45 to-transparent " +
      "text-white text-lg sm:text-xl leading-snug [&_*]:text-white [&_a]:underline [&_p]:m-0 space-y-1"

    const frame = document.createElement("div")
    frame.className = "relative"
    frame.appendChild(this.img)
    frame.appendChild(this.caption)
    frame.appendChild(this.button("Sluiten", "M6 6l12 12M18 6L6 18", "top-2 right-2", () => dialog.close()))

    // Prev/next only make sense with more than one image.
    if (this.imageTargets.length > 1) {
      frame.appendChild(this.button("Vorige", "M15 6l-6 6 6 6", "top-1/2 -translate-y-1/2 left-2", () => this.step(-1)))
      frame.appendChild(this.button("Volgende", "M9 6l6 6-6 6", "top-1/2 -translate-y-1/2 right-2", () => this.step(1)))
    }

    dialog.appendChild(frame)
    dialog.addEventListener("click", (event) => {
      if (event.target === dialog) dialog.close()
    })
    dialog.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft") this.step(-1)
      else if (event.key === "ArrowRight") this.step(1)
    })
    dialog.addEventListener("close", () => {
      dialog.remove()
      this.dialog = this.img = this.caption = null
    })

    this.dialog = dialog
    document.body.appendChild(dialog)
    dialog.showModal()
    this.render()
  }

  step(delta) {
    const count = this.imageTargets.length
    this.index = (this.index + delta + count) % count
    this.render()
  }

  render() {
    const link = this.imageTargets[this.index]
    if (this.img) this.img.src = link.getAttribute("href")
    if (this.caption) {
      const html = link.dataset.caption || ""
      this.caption.innerHTML = html
      this.caption.hidden = html === ""
    }
  }

  // Builds an overlay control: a round button positioned by `place`, with an
  // SVG `path`, an accessible label, and a click handler.
  button(label, path, place, onClick) {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.setAttribute("aria-label", label)
    btn.className =
      `absolute ${place} grid place-items-center w-9 h-9 rounded-full ` +
      "bg-white/90 text-brand-deep shadow-card hover:bg-white transition"
    btn.innerHTML =
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" ' +
      `stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="${path}"/></svg>`
    btn.addEventListener("click", onClick)
    return btn
  }
}
